import { projectAssistantActivity, type AssistantActivityDisplay } from '@digital-twins/shared-types/assistant-activity'
import { ThreadActivityLeg } from './ThreadActivityIndicator'

/**
 * The two stream frames the activity checklist is built from, as the page's
 * stream loops see them. Declared here rather than taken from the shared-types
 * events so the optional `callId` reads the same whether or not the producer
 * sends it yet, and so a reload can feed the reducer without a wire frame.
 */
export interface ActivityStartFrame extends AssistantActivityDisplay {
  type: 'tool_start'
  tool: string
  query?: string
  callId?: string
}

export interface ActivityEndFrame extends AssistantActivityDisplay {
  type: 'tool_end'
  tool: string
  ok: boolean
  itemCount?: number
  callId?: string
  /** Why a leg reported `ok: false`. `empty` is a search that found nothing. */
  errorCode?: string
}

export type ActivityFrame = ActivityStartFrame | ActivityEndFrame

function readCallId(frame: { callId?: unknown }): string | undefined {
  return typeof frame.callId === 'string' && frame.callId.trim()
    ? frame.callId
    : undefined
}

/**
 * Codes for a search that ran and found nothing: `empty` (research search)
 * and `no_passages` (library search). Every other code is a real failure.
 */
const EMPTY_SEARCH_CODES = new Set(['empty', 'no_passages'])

/**
 * A search that found nothing reports `ok: false` with an empty-search code.
 * It is a finished step ("nothing relevant"), not an unavailable one.
 */
function isEmptySearch(errorCode: unknown): boolean {
  return typeof errorCode === 'string' && EMPTY_SEARCH_CODES.has(errorCode)
}

/**
 * Folds one `tool_start` / `tool_end` into the legs a turn has announced.
 * Pure, so both stream loops in `ThreadDetailPage` (the normal turn and the
 * report turn) share one rule set (docs/plans/thread-activity-steps.md, F10).
 *
 * Start appends a running leg in arrival order. A start whose `callId` is
 * already on a leg is a replay and changes nothing.
 *
 * End pairs by `callId` first. When the end carries one and a leg owns it,
 * only that leg is touched, and an end for a leg that already closed is a
 * no-op: python may announce the same call once when it settles and again
 * with its full result. Otherwise (an older producer, or a start that carried
 * no id) the end closes the FIRST OPEN leg of the same tool, never every leg
 * of that tool, so two concurrent web searches close one at a time.
 *
 * An end that pairs with nothing returns the same array, so React skips the
 * render.
 */
export function applyActivityFrame(
  legs: Array<ThreadActivityLeg>,
  frame: ActivityFrame,
): Array<ThreadActivityLeg> {
  const callId = readCallId(frame)

  if (frame.type === 'tool_start') {
    if (callId && legs.some((leg) => leg.callId === callId)) {
      return legs
    }

    const query = typeof frame.query === 'string' ? frame.query.trim() : ''

    const { source_kinds: sourceKinds, ...display } = projectAssistantActivity(frame)

    return [
      ...legs,
      {
        tool: frame.tool,
        done: false,
        ...display,
        ...(query ? { query } : {}),
        ...(sourceKinds ? { sourceKinds } : {}),
        ...(callId ? { callId } : {}),
      },
    ]
  }

  let index = -1
  if (callId) {
    const owner = legs.findIndex((leg) => leg.callId === callId)
    if (owner >= 0) {
      if (legs[owner].done) {
        return legs
      }
      index = owner
    }
  }

  if (index < 0) {
    index = legs.findIndex(
      (leg) =>
        !leg.done &&
        leg.tool === frame.tool &&
        (callId === undefined || leg.callId === undefined),
    )
  }

  if (index < 0) {
    return legs
  }

  const itemCount =
    typeof frame.itemCount === 'number' && Number.isFinite(frame.itemCount)
      ? frame.itemCount
      : undefined

  const { source_kinds: sourceKinds, ...display } = projectAssistantActivity(frame)
  return legs.map((leg, position) =>
    position === index
      ? {
          ...leg,
          done: true,
          ...display,
          ...(sourceKinds ? { sourceKinds } : {}),
          ok: Boolean(frame.ok) || isEmptySearch(frame.errorCode),
          ...(itemCount !== undefined ? { itemCount } : {}),
        }
      : leg,
  )
}

/**
 * A saved `tool_calls` record as the reload path reads it. `item_count` is
 * the additive field Step 1 persists; older rows lack it.
 */
export interface PersistedActivityCall extends AssistantActivityDisplay {
  tool: string
  args_summary?: string | null
  ok?: boolean | null
  item_count?: number | null
  error_code?: string | null
}

/**
 * The same legs, rebuilt from a reloaded message's `toolCalls` (D6). Every
 * persisted call has finished, so each leg is done. A record with no tool
 * name is skipped rather than rendered as an unnamed row.
 */
export function activityLegsFromToolCalls(
  calls: Array<PersistedActivityCall> | null | undefined,
): Array<ThreadActivityLeg> | undefined {
  if (!calls || calls.length === 0) {
    return undefined
  }

  const legs: Array<ThreadActivityLeg> = []
  for (const call of calls) {
    const tool = typeof call.tool === 'string' ? call.tool.trim() : ''
    if (!tool) {
      continue
    }

    const query =
      typeof call.args_summary === 'string' ? call.args_summary.trim() : ''
    const itemCount =
      typeof call.item_count === 'number' && Number.isFinite(call.item_count)
        ? call.item_count
        : undefined

    const { source_kinds: sourceKinds, ...display } = projectAssistantActivity(call)

    legs.push({
      tool,
      done: true,
      ...display,
      ok: call.ok !== false || isEmptySearch(call.error_code),
      ...(query ? { query } : {}),
      ...(sourceKinds ? { sourceKinds } : {}),
      ...(itemCount !== undefined ? { itemCount } : {}),
    })
  }

  return legs.length > 0 ? legs : undefined
}
